<template>
  <v-app>
    <v-navigation-drawer
      v-model="drawer"
      app
      clipped
      color="primary"
      dark
    >
      <v-list>
        <v-list-item>
          <v-list-item-avatar>
            <v-avatar color="white">
              <span class="text-h6 primary--text">{{ userInitials }}</span>
            </v-avatar>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title>{{ userName }}</v-list-item-title>
            <v-list-item-subtitle>{{ userEmail }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :to="item.to"
          link
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <v-list>
          <v-list-item @click="logout" link>
            <v-list-item-icon>
              <v-icon>mdi-logout</v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title>Logout</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <v-app-bar
      app
      clipped-left
      color="white"
      elevation="1"
    >
      <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>

      <v-toolbar-title class="text-h6 font-weight-bold">
        <span class="primary--text">AutoSyncX™️</span> Rental Dashboard
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-btn
        color="primary"
        class="mr-4"
        to="/rental-vehicles"
      >
        <v-icon left>mdi-car</v-icon>
        Browse Vehicles
      </v-btn>
    </v-app-bar>

    <v-main>
      <v-container fluid>
        <v-row>
          <v-col cols="12">
            <v-card class="mb-6">
              <v-card-title class="headline">
                Welcome back, {{ firstName }}!
              </v-card-title>

              <v-card-text>
                <p>Manage your rental bookings and account information from this dashboard.</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-card class="mb-6">
              <v-card-title class="headline">
                <v-icon left color="primary">mdi-calendar-check</v-icon>
                Upcoming Bookings
              </v-card-title>

              <v-card-text>
                <v-data-table
                  :headers="bookingHeaders"
                  :items="futureBookings"
                  :items-per-page="5"
                  :loading="loading"
                  class="elevation-0"
                >
                  <template v-slot:item.vehicle="{ item }">
                    {{ item.vehicle[0].year }} {{ item.vehicle[0].make }} {{ item.vehicle[0].model }}
                  </template>

                  <template v-slot:item.start_datetime="{ item }">
                    {{ formatDate(item.start_datetime) }}
                  </template>

                  <template v-slot:item.end_datetime="{ item }">
                    {{ formatDate(item.end_datetime) }}
                  </template>

                  <template v-slot:item.status="{ item }">
                    <v-chip
                      :color="getStatusColor(item.status)"
                      small
                    >
                      {{ item.status }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      small
                      text
                      color="primary"
                      @click="viewBookingDetails(item)"
                    >
                      View
                    </v-btn>
                  </template>

                  <template v-slot:no-data>
                    <p class="text-center my-3">No upcoming bookings found</p>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="mb-6">
              <v-card-title class="headline">
                <v-icon left color="grey darken-1">mdi-history</v-icon>
                Past Bookings
              </v-card-title>

              <v-card-text>
                <v-data-table
                  :headers="bookingHeaders"
                  :items="pastBookings"
                  :items-per-page="5"
                  :loading="loading"
                  class="elevation-0"
                >
                  <template v-slot:item.vehicle="{ item }">
                    {{ item.vehicle[0].year }} {{ item.vehicle[0].make }} {{ item.vehicle[0].model }}
                  </template>

                  <template v-slot:item.start_datetime="{ item }">
                    {{ formatDate(item.start_datetime) }}
                  </template>

                  <template v-slot:item.end_datetime="{ item }">
                    {{ formatDate(item.end_datetime) }}
                  </template>

                  <template v-slot:item.status="{ item }">
                    <v-chip
                      :color="getStatusColor(item.status)"
                      small
                    >
                      {{ item.status }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      small
                      text
                      color="primary"
                      @click="viewBookingDetails(item)"
                    >
                      View
                    </v-btn>
                  </template>

                  <template v-slot:no-data>
                    <p class="text-center my-3">No past bookings found</p>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title class="headline">
                <v-icon left color="primary">mdi-account-cog</v-icon>
                Account Settings
              </v-card-title>

              <v-card-text>
                <v-form ref="form" v-model="valid">
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="userProfile.first_name"
                        label="First Name"
                        outlined
                        dense
                        :rules="nameRules"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="userProfile.last_name"
                        label="Last Name"
                        outlined
                        dense
                        :rules="nameRules"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="userProfile.email"
                        label="Email"
                        outlined
                        dense
                        disabled
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="userProfile.phone_m"
                        label="Phone"
                        outlined
                        dense
                        :rules="phoneRules"
                        v-mask="'(###) ###-####'"
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-divider class="my-4"></v-divider>

                  <v-row>
                    <v-col cols="12">
                      <v-btn
                        color="primary"
                        @click="updateProfile"
                        :loading="updatingProfile"
                        :disabled="!valid || updatingProfile"
                      >
                        Update Profile
                      </v-btn>

                      <v-btn
                        color="secondary"
                        class="ml-4"
                        @click="changePasswordDialog = true"
                      >
                        Change Password
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Booking Details Dialog -->
      <v-dialog v-model="bookingDetailsDialog" max-width="700px">
        <v-card v-if="selectedBooking">
          <v-card-title class="headline">
            Booking Details
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-img
                  v-if="selectedBooking.vehicle && selectedBooking.vehicle[0] && selectedBooking.vehicle[0].images && selectedBooking.vehicle[0].images.length > 0"
                  :src="selectedBooking.vehicle[0].images[0].image"
                  height="200"
                  contain
                ></v-img>

                <div v-else class="grey lighten-3 d-flex justify-center align-center" style="height: 200px;">
                  <v-icon size="64" color="grey">mdi-car</v-icon>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <h3 class="text-h6 mb-2">
                  {{ selectedBooking.vehicle[0].year }} {{ selectedBooking.vehicle[0].make }} {{ selectedBooking.vehicle[0].model }}
                </h3>

                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon>mdi-calendar-range</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ formatDate(selectedBooking.start_datetime) }} - {{ formatDate(selectedBooking.end_datetime) }}
                      </v-list-item-title>
                      <v-list-item-subtitle>Booking Period</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon>mdi-map-marker</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ selectedBooking.vehicle[0].address }}, {{ selectedBooking.vehicle[0].city }}, {{ selectedBooking.vehicle[0].state }} {{ selectedBooking.vehicle[0].zip }}
                      </v-list-item-title>
                      <v-list-item-subtitle>Pickup Location</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon>mdi-currency-usd</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        ${{ selectedBooking.total_amount }}
                      </v-list-item-title>
                      <v-list-item-subtitle>Total Amount</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon>mdi-tag</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        ${{ selectedBooking.discount_amount }}
                      </v-list-item-title>
                      <v-list-item-subtitle>Discount Applied</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon>mdi-check-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-chip
                          :color="getStatusColor(selectedBooking.status)"
                          small
                        >
                          {{ selectedBooking.status }}
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle>Status</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="bookingDetailsDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Change Password Dialog -->
      <v-dialog v-model="changePasswordDialog" max-width="500px">
        <v-card>
          <v-card-title class="headline">
            Change Password
          </v-card-title>

          <v-card-text>
            <v-form ref="passwordForm" v-model="passwordValid">
              <v-text-field
                v-model="passwordData.current_password"
                label="Current Password"
                :type="showCurrentPassword ? 'text' : 'password'"
                :append-icon="showCurrentPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="showCurrentPassword = !showCurrentPassword"
                :rules="passwordRules"
                outlined
                dense
              ></v-text-field>

              <v-text-field
                v-model="passwordData.new_password"
                label="New Password"
                :type="showNewPassword ? 'text' : 'password'"
                :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="showNewPassword = !showNewPassword"
                :rules="passwordRules"
                outlined
                dense
              ></v-text-field>

              <v-text-field
                v-model="passwordData.confirm_password"
                label="Confirm New Password"
                :type="showConfirmPassword ? 'text' : 'password'"
                :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="showConfirmPassword = !showConfirmPassword"
                :rules="[
                  v => !!v || 'Confirm password is required',
                  v => v === passwordData.new_password || 'Passwords do not match'
                ]"
                outlined
                dense
              ></v-text-field>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="changePasswordDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              @click="updatePassword"
              :loading="updatingPassword"
              :disabled="!passwordValid || updatingPassword"
            >
              Update Password
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Success Snackbar -->
      <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        timeout="3000"
      >
        {{ snackbarText }}

        <template v-slot:action="{ attrs }">
          <v-btn
            text
            v-bind="attrs"
            @click="snackbar = false"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<script>
// Import specific functions from date-fns to avoid optional chaining issues
import format from 'date-fns/format'

export default {
  name: 'RentalDashboard',

  data() {
    return {
      drawer: true,
      loading: true,
      updatingProfile: false,
      updatingPassword: false,
      valid: true,
      passwordValid: true,
      bookingDetailsDialog: false,
      changePasswordDialog: false,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      selectedBooking: null,
      pastBookings: [],
      futureBookings: [],

      userProfile: {
        first_name: '',
        last_name: '',
        email: '',
        phone_m: ''
      },

      passwordData: {
        current_password: '',
        new_password: '',
        confirm_password: ''
      },

      menuItems: [
        { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/rental/dashboard' },
        { title: 'Browse Vehicles', icon: 'mdi-car', to: '/rental-vehicles' },
        { title: 'My Bookings', icon: 'mdi-calendar-check', to: '/rental/bookings' }
      ],

      bookingHeaders: [
        { text: 'Vehicle', value: 'vehicle', sortable: false },
        { text: 'Start Date', value: 'start_datetime' },
        { text: 'End Date', value: 'end_datetime' },
        { text: 'Status', value: 'status' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],

      nameRules: [
        v => !!v || 'Name is required'
      ],

      phoneRules: [
        v => !!v || 'Phone number is required',
        v => v.replace(/\D/g, '').length === 10 || 'Phone number must be valid'
      ],

      passwordRules: [
        v => !!v || 'Password is required',
        v => v.length >= 6 || 'Password must be at least 6 characters'
      ]
    }
  },

  computed: {
    userInitials() {
      return this.userProfile.first_name.charAt(0) + this.userProfile.last_name.charAt(0)
    },

    userName() {
      return `${this.userProfile.first_name} ${this.userProfile.last_name}`
    },

    userEmail() {
      return this.userProfile.email
    },

    firstName() {
      return this.userProfile.first_name
    }
  },

  created() {
    this.loadUserProfile()
    this.loadBookings()
  },

  methods: {
    loadUserProfile() {
      const userData = localStorage.getItem('rental_user')
      if (userData) {
        const user = JSON.parse(userData)
        this.userProfile = {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          phone_m: user.phone_m
        }
      } else {
        this.$router.push('/rental/login')
      }
    },

    loadBookings() {
      this.loading = true

      const token = localStorage.getItem('rental_token')
      if (!token) {
        this.$router.push('/rental/login')
        return
      }

      axios.get('/api/rental/bookings', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.data.flag === 1) {
          this.pastBookings = response.data.past_bookings || []
          this.futureBookings = response.data.future_bookings || []
        } else {
          this.showSnackbar('Failed to load bookings', 'error')
        }
      })
      .catch(error => {
        console.error('Error loading bookings:', error)

        if (error.response && error.response.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('rental_token')
          localStorage.removeItem('rental_user')
          this.$router.push('/rental/login')
        } else {
          this.showSnackbar('Error loading bookings', 'error')
        }
      })
      .finally(() => {
        this.loading = false
      })
    },

    updateProfile() {
      if (this.$refs.form.validate()) {
        this.updatingProfile = true

        const token = localStorage.getItem('rental_token')
        if (!token) {
          this.$router.push('/rental/login')
          return
        }

        axios.post('/api/rental/update-profile', this.userProfile, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then(response => {
          if (response.data.flag === 1) {
            // Update local storage
            const userData = JSON.parse(localStorage.getItem('rental_user'))
            userData.first_name = this.userProfile.first_name
            userData.last_name = this.userProfile.last_name
            userData.phone_m = this.userProfile.phone_m
            localStorage.setItem('rental_user', JSON.stringify(userData))

            this.showSnackbar('Profile updated successfully')
          } else {
            this.showSnackbar('Failed to update profile', 'error')
          }
        })
        .catch(error => {
          console.error('Error updating profile:', error)
          this.showSnackbar('Error updating profile', 'error')
        })
        .finally(() => {
          this.updatingProfile = false
        })
      }
    },

    updatePassword() {
      if (this.$refs.passwordForm.validate()) {
        this.updatingPassword = true

        const token = localStorage.getItem('rental_token')
        if (!token) {
          this.$router.push('/rental/login')
          return
        }

        axios.post('/api/rental/change-password', {
          current_password: this.passwordData.current_password,
          new_password: this.passwordData.new_password
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then(response => {
          if (response.data.flag === 1) {
            this.changePasswordDialog = false
            this.passwordData = {
              current_password: '',
              new_password: '',
              confirm_password: ''
            }
            this.showSnackbar('Password changed successfully')
          } else {
            this.showSnackbar(response.data.message || 'Failed to change password', 'error')
          }
        })
        .catch(error => {
          console.error('Error changing password:', error)
          this.showSnackbar(error.response?.data?.message || 'Error changing password', 'error')
        })
        .finally(() => {
          this.updatingPassword = false
        })
      }
    },

    viewBookingDetails(booking) {
      this.selectedBooking = booking
      this.bookingDetailsDialog = true
    },

    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        // Check if date is valid before formatting
        if (isNaN(date.getTime())) {
          return dateString;
        }
        return format(date, 'MMM d, yyyy h:mm a');
      } catch (error) {
        console.error('Date formatting error:', error);
        return dateString;
      }
    },

    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'confirmed':
          return 'success'
        case 'pending':
          return 'warning'
        case 'cancelled':
          return 'error'
        default:
          return 'grey'
      }
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },

    logout() {
      localStorage.removeItem('rental_token')
      localStorage.removeItem('rental_user')
      this.$router.push('/rental/login')
    }
  }
}
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
  min-height: 250px;
}
</style>
